import {dataTestId} from '@hconnect/uikit'
import {IconButtonWithTooltip} from '@hconnect/uikit/src/lib2'
import {
  CancelOutlined,
  ReportOutlined,
  WarningAmber,
  InfoOutlined,
  DangerousOutlined
} from '@mui/icons-material'
import {Stack, CircularProgress, Typography} from '@mui/material'
import Markdown from 'markdown-to-jsx'
import moment from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {OptimizerStatus, PlannerFeature} from '../../../shared/enums'
import {useCancelOptimizerRun} from '../../../shared/hooks/api'
import {usePlannerFeature} from '../../../shared/hooks/usePlannerFeature'
import {usePlantConfig} from '../../../shared/hooks/usePlantConfigData'
import {getLocalizedDayMonthTimeFormat} from '../../../shared/selectors/time'
import {OptimizerScheduleType} from '../shared/enums/optimizerSchedules'

import {OptimizerSchedule} from './hooks/useOptimizerPlansOverview'

const statusToIconMap = {
  [OptimizerStatus.Error]: (
    <ReportOutlined fontSize="small" color="error" {...dataTestId('error_icon')} />
  ),
  [OptimizerStatus.Failed]: (
    <DangerousOutlined fontSize="small" color="error" {...dataTestId('failed_icon')} />
  ),
  [OptimizerStatus.Warning]: (
    <WarningAmber fontSize="small" color="warning" {...dataTestId('warning_icon')} />
  ),
  [OptimizerStatus.Success]: <InfoOutlined fontSize="small" {...dataTestId('info_icon')} />
}

export function OptimizerPlansPlanNameCell({schedule}: {schedule: OptimizerSchedule}) {
  const {timezone_id: timezoneId} = usePlantConfig()
  const {
    t,
    i18n: {language}
  } = useTranslation()

  const {cancelOptimizerRun, isLoading} = useCancelOptimizerRun()
  const shouldShowWarning = usePlannerFeature(PlannerFeature.OptimizerStatusDisplay)

  if (schedule.type === OptimizerScheduleType.Manual) {
    const {
      lastSaved: {savedBy, savedAt}
    } = schedule
    return (
      <>
        {t('optimizer.manualScheduleName', {
          savedBy: savedBy,
          savedOn: getLocalizedDayMonthTimeFormat(moment.utc(savedAt), timezoneId, language)
        })}
      </>
    )
  }

  if (schedule.type === OptimizerScheduleType.Optimized) {
    const {
      optimizationDetails: {triggeredBy, triggeredOn, name, message, optimizerStatus}
    } = schedule

    // We always show a warning for failed optimized plans and all other warnings
    // for plants with optimizer_status_display feature enabled
    const shouldShowOptimizerWarningIcon =
      (shouldShowWarning ||
        schedule.optimizationDetails.optimizerStatus === OptimizerStatus.Failed) &&
      message

    return (
      <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
        <span>
          {t('optimizer.optimizerScheduleName', {
            name,
            user: triggeredBy,
            date: getLocalizedDayMonthTimeFormat(moment.utc(triggeredOn), timezoneId, language)
          })}
        </span>
        {shouldShowOptimizerWarningIcon && (
          <IconButtonWithTooltip
            tooltipContent={
              <Typography variant="body1" {...dataTestId('optimizer_warning_tooltip')}>
                <Markdown>{message}</Markdown>
              </Typography>
            }
            icon={statusToIconMap[optimizerStatus]}
            {...dataTestId('optimizer_warning_button')}
          />
        )}
      </Stack>
    )
  }

  const {queuedAt, queuedBy, name} = schedule

  return (
    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
      <CircularProgress size={16} />
      <Stack>
        <span>
          {t('optimizer.optimizerScheduleName', {
            name: name,
            user: queuedBy,
            date: getLocalizedDayMonthTimeFormat(moment.utc(queuedAt), timezoneId, language)
          })}
        </span>
        <Typography variant="caption">{t('optimizer.calculating')}</Typography>
      </Stack>
      <IconButtonWithTooltip
        {...dataTestId('cancel_optimizer_run_button')}
        onClick={() => cancelOptimizerRun(schedule.optimizerRunId)}
        disabled={isLoading}
        icon={<CancelOutlined fontSize="small" />}
        tooltipContent={t('optimizer.cancelOptimizerRun')}
      />
    </Stack>
  )
}
